import { Download, FileText, ShieldCheck, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { IMAGES, DOCUMENTS } from "@/data/site";

const PILLARS = [
  { icon: ShieldCheck, title: "Trasparenza", text: "Rendiamo pubblici i documenti che regolano e rendicontano la vita del club." },
  { icon: FileText, title: "Chiarezza", text: "Statuto e bilanci sempre consultabili e scaricabili in formato PDF." },
  { icon: Download, title: "Accessibilità", text: "Ogni socio può accedere in ogni momento alla documentazione ufficiale." },
];

export default function StatutoBilancio() {
  return (
    <div data-testid="statuto-bilancio-page">
      <PageHero
        image={IMAGES.statutoBg}
        overline="Documenti ufficiali"
        titleLines={["Statuto e", "Bilancio"]}
        subtitle="Crediamo in una gestione trasparente e responsabile. Qui trovi e scarichi tutti i documenti istituzionali del Club Atletico Bologna."
      />

      {/* Intro pillars */}
      <section className="bg-cream py-20 md:py-28" data-testid="statuto-intro">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading
            overline="Il nostro impegno"
            title="La trasparenza al centro"
            subtitle="Come associazione sportiva dilettantistica, mettiamo a disposizione dei soci e della comunità tutti i documenti che raccontano come operiamo."
          />
          <Stagger className="mt-14 grid gap-6 md:grid-cols-3">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <StaggerItem key={title}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-8">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-cab/10 text-cab">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-extrabold uppercase tracking-tight text-ink">
                    {title}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Documents */}
      <section className="bg-white py-20 md:py-32" data-testid="statuto-documenti">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <SectionHeading overline="Area download" title="Documenti da scaricare" />
          <Stagger className="mt-14 grid gap-6 md:grid-cols-2">
            {DOCUMENTS.map((doc) => (
              <StaggerItem key={doc.file}>
                <div className="group flex h-full flex-col justify-between gap-6 rounded-2xl border border-ink/10 bg-cream p-7 transition-all duration-500 hover:-translate-y-1 hover:border-cab/40 hover:shadow-[0_20px_50px_rgba(62,106,158,0.12)] md:flex-row md:items-center">
                  <div className="flex items-start gap-5">
                    <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-cab text-cream">
                      <FileText className="h-7 w-7" />
                    </span>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-cab/10 px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.15em] text-cab">
                          {doc.category}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {doc.year} · PDF · {doc.size}
                        </span>
                      </div>
                      <h3 className="mt-2 font-display text-xl font-extrabold tracking-tight text-ink">
                        {doc.title}
                      </h3>
                      <p className="text-sm text-cab">{doc.subtitle}</p>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
                        {doc.desc}
                      </p>
                    </div>
                  </div>
                  <a
                    href={doc.file}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`download-${doc.file.split("/").pop().replace(".pdf", "")}`}
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-cab px-6 py-3.5 font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-cab-dark"
                  >
                    Scarica
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal delay={0.1}>
            <p className="mt-10 flex items-center gap-2 text-sm text-muted-foreground">
              <ArrowUpRight className="h-4 w-4 text-cab" />
              Per richiedere ulteriori documenti o informazioni, contatta la segreteria del club.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
